// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * useGlideText — constant-glide replacement for useStreamingText.
 *
 * Same API as useStreamingText ({ displayedText, isAnimating, skipToEnd }),
 * drop-in swap in TypewriterText.
 *
 * Difference vs the old hook: instead of measuring the last 8 chunks and
 * revealing at 85% of that rate (with discrete 2x/3x jumps when backlog
 * grows), the reveal rate is a PROPORTIONAL CONTROLLER on the backlog with
 * exponential smoothing:
 *
 *   targetRate = clamp(backlog / TAU, minRate, maxRate)
 *   rate      += (targetRate - rate) * (dt / RATE_SMOOTH_MS)
 *
 * The rate converges smoothly to (just under) the incoming rate — a constant
 * glide like ChatGPT — instead of oscillating with the chunk cadence. When
 * the stream ends, the remaining backlog is drained over ~DRAIN_MS with an
 * ease-out, so there is no final "burst".
 *
 * ROLLBACK: in thread.jsx TypewriterText, swap useGlideText back to
 * useStreamingText and delete this file.
 */
export function useGlideText(incomingText, baseRate = 5) {
  const [displayedText, setDisplayedText] = useState('')
  const [isAnimating, setIsAnimating] = useState(false)
  const incomingTextRef = useRef('')
  const animationFrameRef = useRef(null)
  const displayIndexRef = useRef(0)
  const lastRenderTimeRef = useRef(0)
  const lastIncomingLenRef = useRef(0)
  const skipRequestedRef = useRef(false)
  const isRunningRef = useRef(false)
  const streamDoneRef = useRef(false)
  const rateRef = useRef(0)   // current reveal rate — chars per ms (smoothed)
  const fracRef = useRef(0)   // fractional char carry between frames

  // --- controller tuning ---
  const MIN_RATE = Math.max(baseRate, 2) / 33 // chars/ms — old "no data" pace
  const MAX_RATE = 3                          // hard cap (chars/ms)
  const TAU = 600                             // backlog time constant (ms):
                                              // steady-state lag behind the LLM
  const RATE_SMOOTH_MS = 150                  // rate changes spread over this
  const DRAIN_MS = 450                        // smooth-finish budget at stream end

  const runAnimation = useCallback(() => {
    if (isRunningRef.current) return
    isRunningRef.current = true

    const animate = (timestamp) => {
      if (skipRequestedRef.current) {
        isRunningRef.current = false
        setIsAnimating(false)
        return
      }

      const elapsed = timestamp - lastRenderTimeRef.current
      if (elapsed < 16) { // full-rate glide; parse cost is low (incremental markdown)
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }
      const dt = Math.min(elapsed, 100)
      lastRenderTimeRef.current = timestamp

      const currentIndex = displayIndexRef.current
      const targetText = incomingTextRef.current
      const backlog = targetText.length - currentIndex

      if (backlog <= 0) {
        // Caught up — wait one more frame in case more text arrives
        animationFrameRef.current = requestAnimationFrame((ts) => {
          if (displayIndexRef.current < incomingTextRef.current.length) {
            animate(ts)
          } else {
            animationFrameRef.current = null
            isRunningRef.current = false
            setIsAnimating(false)
          }
        })
        return
      }

      setIsAnimating(true)

      // ---- constant-glide rate controller ----
      let targetRate = backlog / TAU
      if (streamDoneRef.current) {
        // Stream ended: finish smoothly within ~DRAIN_MS, easing out as the
        // backlog shrinks (no final burst).
        targetRate = Math.max(targetRate, backlog / DRAIN_MS)
      }
      targetRate = Math.max(MIN_RATE, Math.min(MAX_RATE, targetRate))
      const alpha = Math.min(1, dt / RATE_SMOOTH_MS)
      rateRef.current += (targetRate - rateRef.current) * alpha

      // Accumulate fractional chars so low rates still move every few frames
      fracRef.current += rateRef.current * dt
      let whole = Math.floor(fracRef.current)
      if (whole < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }
      fracRef.current -= whole

      // Word boundary: finish the word only if it naturally falls inside this
      // frame's reveal (no artificial extension → no cadence distortion)
      const remaining = targetText.slice(currentIndex)
      const nextSpace = remaining.indexOf(' ')
      const nextNewline = remaining.indexOf('\n')
      if (nextSpace > 0 && nextSpace <= whole) {
        whole = nextSpace + 1
      } else if (nextNewline > 0 && nextNewline <= whole) {
        whole = nextNewline + 1
      }

      // Markdown tokens: reveal complete formatting at once (prevents literal
      // "**" flashes); a token is a handful of chars — invisible at glide rate
      const nextChar = targetText[currentIndex]
      if (nextChar === '*' || nextChar === '_' || nextChar === '`' || nextChar === '#') {
        const match = remaining.match(/^(\*\*?|__?|`+|#+)([^*_`#\n]*?)(\1)/)
        if (match) {
          whole = Math.max(whole, match[0].length)
        }
      }

      displayIndexRef.current = Math.min(currentIndex + whole, targetText.length)
      setDisplayedText(targetText.slice(0, displayIndexRef.current))

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animationFrameRef.current = requestAnimationFrame(animate)
  }, [MIN_RATE])

  const skipToEnd = useCallback(() => {
    skipRequestedRef.current = true
    isRunningRef.current = false
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    displayIndexRef.current = incomingTextRef.current.length
    fracRef.current = 0
    setDisplayedText(incomingTextRef.current)
    setIsAnimating(false)
  }, [])

  // When new text arrives, ensure animation is running
  useEffect(() => {
    const prevLen = lastIncomingLenRef.current
    incomingTextRef.current = incomingText
    lastIncomingLenRef.current = incomingText.length

    if (incomingText.length > prevLen) {
      streamDoneRef.current = false
    }

    if (skipRequestedRef.current) {
      displayIndexRef.current = incomingText.length
      setDisplayedText(incomingText)
      return
    }

    if (displayIndexRef.current < incomingText.length) {
      runAnimation()
    }
  }, [incomingText, runAnimation])

  // Detect stream end: if no new text for 600ms, switch to smooth-drain mode
  useEffect(() => {
    if (!incomingText || streamDoneRef.current) return

    const timer = setTimeout(() => {
      if (lastIncomingLenRef.current === incomingText.length) {
        streamDoneRef.current = true
      }
    }, 600)

    return () => clearTimeout(timer)
  }, [incomingText])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      isRunningRef.current = false
    }
  }, [])

  // Reset when text resets (new message)
  useEffect(() => {
    if (!incomingText) {
      displayIndexRef.current = 0
      lastIncomingLenRef.current = 0
      setDisplayedText('')
      setIsAnimating(false)
      skipRequestedRef.current = false
      streamDoneRef.current = false
      isRunningRef.current = false
      rateRef.current = 0
      fracRef.current = 0
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [incomingText])

  return { displayedText, isAnimating, skipToEnd }
}
